/**
 * Article Creator Requests Service
 *
 * Handles article creation requests for tracking and history.
 */

import { api } from './api';

// Request status types
export type ArticleCreatorRequestStatus =
  | 'pending'
  | 'fetching'
  | 'generating'
  | 'completed'
  | 'failed';

// Request types
export interface ArticleCreatorRequest {
  id: string;
  companyId: string;
  status: ArticleCreatorRequestStatus;
  sourceUrl?: string;
  sourceContent?: string;
  template?: string;
  generatedTitle?: string;
  generatedContent?: string;
  generatedSummary?: string;
  error?: {
    step: string;
    message: string;
  };
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  completedAt?: string;
}

export interface CreateRequestInput {
  sourceUrl?: string;
  sourceContent?: string;
  template?: string;
}

export interface RequestsListResponse {
  requests: ArticleCreatorRequest[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

const REQUESTS_ENDPOINT = '/article-creator/requests';

export const RequestsService = {
  /**
   * Get paginated list of article creation requests
   */
  async getRequests(page = 1, limit = 20): Promise<RequestsListResponse> {
    const response = await api.get<ArticleCreatorRequest[]>(REQUESTS_ENDPOINT, { page, limit });
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch requests');
    }
    return {
      requests: response.data || [],
      meta: {
        total: response.meta?.total ?? 0,
        page: response.meta?.page ?? page,
        limit: response.meta?.limit ?? limit,
      },
    };
  },

  /**
   * Get a single request by ID
   */
  async getRequest(id: string): Promise<ArticleCreatorRequest> {
    const response = await api.get<ArticleCreatorRequest>(`${REQUESTS_ENDPOINT}/${id}`);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch request');
    }
    return response.data;
  },

  /**
   * Create a new article creation request
   */
  async createRequest(input: CreateRequestInput): Promise<ArticleCreatorRequest> {
    const response = await api.post<ArticleCreatorRequest>(REQUESTS_ENDPOINT, input);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create request');
    }
    return response.data;
  },

  /**
   * Delete an article creation request
   */
  async deleteRequest(id: string): Promise<void> {
    const response = await api.delete(`${REQUESTS_ENDPOINT}/${id}`);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete request');
    }
  },
};

export default RequestsService;
